import { Button, ButtonProps, Card } from "@jhool-io/fe-components";
import { cn } from "../../utils/helpers";
import XCircleIcon from "../../icons/XCircle";

interface ListStateProps {
    /** Class for adding more styling to wrapper card */
    cardClass?: string;
    /** Class for styling state component inner container */
    wrapperClass?: string;
    /** Text to display useful information about the current state */
    stateHelperText: string;
    /** Check if loading of list resulted in an error */
    isError: boolean;
    /** Error message to display if loading of list resulted in an error */
    errorMsg?: string;
    /** Empty state message */
    emptyMessage?: string;
    /** List header to display */
    listHeader?: React.ReactNode;
    /** Empty state button props */
    emptyBtnProps?: {
        /** Determines if button should be shown */
        showButton: boolean;
        /** Onclick function for empty state button */
        onButtonClick?: () => void;
        /** Button text */
        buttonText?: React.ReactNode;
        disabled?: boolean;
        buttonClass?: string;
        variant?: ButtonProps["variant"];
    };
    context?: "table" | "general";
}

export default function ListState({
    stateHelperText,
    isError,
    errorMsg,
    listHeader,
    wrapperClass,
    emptyBtnProps,
    emptyMessage,
    cardClass,
    context = "table",
}: ListStateProps) {
    if (context === "general") {
        return (
            <article
                className={cn(
                    `flex items-center h-full flex-col justify-center py-[--padding-card-y] px-[--padding-card-x] bg-[--card-bg]`,
                    { "h-[200px] w-full": !cardClass },
                    cardClass
                )}
            >
                <div
                    className={cn(
                        "h-auto flex items-center justify-content flex-col",
                        wrapperClass
                    )}
                >
                    {isError && (
                        <span role="presentation">
                            <XCircleIcon className="size-6" />
                        </span>
                    )}

                    <p className="font-semibold text-base mb-1 text-center !text-text">
                        {isError && errorMsg}
                        {!isError && emptyMessage}
                    </p>
                    <p
                        className={cn(
                            `max-w-full w-56 font-regular !text-textSec text-caption text-center leading-none`
                        )}
                    >
                        {stateHelperText}
                    </p>
                    {!isError && emptyBtnProps?.showButton && (
                        <Button
                            variant={emptyBtnProps.variant || "primary"}
                            onClick={emptyBtnProps.onButtonClick}
                            className={cn(`mt-3`, emptyBtnProps.buttonClass)}
                            disabled={emptyBtnProps.disabled}
                        >
                            {emptyBtnProps.buttonText}
                        </Button>
                    )}
                </div>
            </article>
        );
    }

    return (
        <Card classNames={cn("border-none", cardClass)}>
            {listHeader && (
                <h3 className="font-medium text-caption uppercase">
                    {listHeader}
                </h3>
            )}
            <div
                className={cn(
                    "h-[200px] flex items-center flex-col justify-center",
                    wrapperClass
                )}
            >
                {isError && (
                    <span role="presentation">
                        <svg
                            viewBox="0 0 24 24"
                            width="24"
                            height="24"
                            stroke="currentColor"
                            strokeWidth="2"
                            fill="none"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="size-6 stroke-danger"
                        >
                            <circle cx="12" cy="12" r="10" />
                            <line x1="15" y1="9" x2="9" y2="15" />
                            <line x1="9" y1="9" x2="15" y2="15" />
                        </svg>
                    </span>
                )}
                <p className="font-semibold text-base mb-1 text-center !text-text">
                    {isError && errorMsg}
                    {!isError && emptyMessage}
                </p>
                <p
                    className={cn(
                        `max-w-full w-56 font-regular !text-textSec text-caption text-center leading-none`
                    )}
                >
                    {stateHelperText}
                </p>
                {!isError && emptyBtnProps?.showButton && (
                    <Button
                        variant={emptyBtnProps.variant || "primary"}
                        size="small"
                        onClick={emptyBtnProps.onButtonClick}
                        className="mt-3"
                        disabled={emptyBtnProps.disabled}
                    >
                        {emptyBtnProps.buttonText}
                    </Button>
                )}
            </div>
        </Card>
    );
}
